const About = () => {
  const pillars = [
    {
      title: 'Full-Stack Engineering',
      description: 'Building robust, end-to-end applications that bridge the gap between complex logic and intuitive interfaces.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    },
    {
      title: 'Cybersecurity Mindset',
      description: 'Applying a security-first approach to every line of code, ensuring data integrity and user protection.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: 'Scalable Innovation',
      description: 'Leveraging modern stacks like Nest.js and AWS to build systems that grow alongside the users they serve.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen pt-20 py-16 sm:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-block px-3 py-1 rounded-full bg-purple-secondary/10 border border-purple-secondary/30 text-purple-accent text-xs sm:text-sm font-medium mb-4">
            My Story
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-8 gradient-text-enhanced">
            Beyond the Code.
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div className="space-y-6 text-purple-light/90 text-sm sm:text-base lg:text-lg leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <p>
              Hey there! I'm <span className="text-purple-secondary font-bold text-xl sm:text-2xl">Pathum Vimukthi</span>. My journey into the digital realm wasn't just about learning syntax; it was about discovering the power to solve real-world problems through engineering.
            </p>
            <p>
              I work across the entire stack backend and frontend building APIs, databases, and polished user interfaces with a security first approach. I believe a great application is secure by design, fast, and built to scale.
            </p>
            <p>
              I handle everything from server-side architecture to client-side UX, and I regularly perform penetration testing to harden systems against modern threats. Every bug is a puzzle and every vulnerability is an opportunity to learn.
            </p>
            <div className="pt-6">
              <div className="flex items-center gap-6">
                <div>
                  <h4 className="text-3xl font-bold text-white">7+</h4>
                  <p className="text-xs text-purple-accent uppercase tracking-widest">Major Projects</p>
                </div>
                <div className="w-px h-10 bg-purple-secondary/30"></div>
                <div>
                  <h4 className="text-3xl font-bold text-white">3+</h4>
                  <p className="text-xs text-purple-accent uppercase tracking-widest">Years Coding</p>
                </div>
                <div className="w-px h-10 bg-purple-secondary/30"></div>
                <div>
                  <h4 className="text-3xl font-bold text-white">🚀</h4>
                  <p className="text-xs text-purple-accent uppercase tracking-widest">Infinite Passion</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            {pillars.map((pillar, index) => (
              <div key={index} className="glass-card p-6 rounded-2xl border border-purple-secondary/20 hover:border-purple-secondary/50 transition-all duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-purple-secondary/10 text-purple-secondary group-hover:bg-purple-secondary group-hover:text-white transition-all duration-300">
                    {pillar.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
                    <p className="text-sm text-purple-light/70 leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="mt-12 animate-fadeInUp">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 gradient-text-enhanced">Education</h2>
          <div className="glass-card p-6 rounded-2xl border border-purple-secondary/20 hover:border-purple-secondary/50 transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-purple-secondary/10 text-purple-secondary group-hover:bg-purple-secondary group-hover:text-white transition-all duration-300">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422M12 14v6M12 20l-6.16-3.422" /></svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">B.Eng. (Hons)  Computer Engineering (Undergraduate)</h3>
                <p className="text-sm text-purple-light/70 leading-relaxed mb-2">University of Ruhuna Faculty of Engineering</p>
                <p className="text-xs text-purple-accent/80 uppercase tracking-wide">Department of Electrical & Information Engineering</p>
              </div>
            </div>
          </div>
        </div>

       
      </div>
    </div>
  );
};

export default About;
